<template>
  <div class="flex flex-col">
    <FilevaliditySkeleton v-if="isLoading"></FilevaliditySkeleton>
    <template v-else>
      <Form
        :initial-values="initialValues"
        :keep-values="true"
        class="flex flex-col gap-y-5 my-[1.8rem]"
        @submit="(values) => onSubmit(values)"
        v-slot="{ values }"
        ref="form"
      >
        <CollapsibleSection
          :header-label="$t('identificationInformation')"
          body-class="w-full flex flex-col gap-y-3"
          disabled
        >
          <div class="w-full h-full my-3">
            <InfoBar>
              <span class="flex-1 text-justify my-3 text-white text-base h-full w-full">
                {{ $t('onArrivalVisaNoteGuest') }}
              </span>
            </InfoBar>
          </div>

          <!-- form start here -->
          <div
            class="flex-row md:items-center md:flex-row w-full flex gap-x-4 gap-y-1 text-brand-black-secondary md:flex-nowrap xs:flex-wrap"
          >
            <div class="md:w-1/4 xs:w-full">
              <p>{{ $t('selectIdentificater') }} <span class="text-xs text-red-500">*</span></p>
            </div>
            <div class="md:flex xs:block w-full gap-4">
              <Field
                :value="1"
                name="selectFileIdentificater"
                type="radio"
                v-slot="{ field, resetField }"
              >
                <label class="flex gap-x-2 cursor-pointer">
                  <input
                    class="accent-primary"
                    type="radio"
                    name="selectFileIdentificater"
                    @click="() => handleRadioClick(resetField, 1)"
                    v-bind="field"
                  />
                  {{ $t('fileVisaNumber') }}
                </label>
              </Field>

              <Field
                :value="2"
                name="selectFileIdentificater"
                type="radio"
                v-slot="{ field, resetField }"
              >
                <label class="flex gap-x-2 cursor-pointer">
                  <input
                    class="accent-primary"
                    type="radio"
                    name="selectFileIdentificater"
                    @click="() => handleRadioClick(resetField, 2)"
                    v-bind="field"
                  />
                  {{ $t('unifiedNumber') }}
                </label>
              </Field>
            </div>
          </div>

          <!-- fileVisaNumber -->
          <div class="flex flex-col gap-y-0.5" v-if="values.selectFileIdentificater === 1">
            <label class="text-brand-black-secondary">{{ $t('fileVisaNumber') }}</label>
            <div class="flex flex-col items-top md:flex-row">
              <Field
                :rules="isRequired"
                name="departmentCode"
                class="flex flex-col gap-y-1"
                v-slot="{ field, errors: errs }"
              >
                <SimpleTextInput
                  :field="field"
                  :errors="errs"
                  :placeholder="$t('department')"
                  mask="###"
                />
              </Field>
              <div class="px-2 pt-1.5">/</div>
              <Field
                :rules="isRequired"
                name="serviceYear"
                class="flex flex-col gap-y-1"
                v-slot="{ field, errors: errs }"
              >
                <SimpleTextInput
                  :field="field"
                  :errors="errs"
                  :placeholder="$t('year')"
                  mask="###"
                />
              </Field>
              <div class="px-2 pt-1.5">/</div>
              <Field
                :rules="isRequired"
                name="serviceCode"
                class="flex flex-col gap-y-1"
                v-slot="{ field, errors: errs }"
              >
                <SimpleTextInput
                  :field="field"
                  :errors="errs"
                  :placeholder="$t('Category')"
                  mask="###"
                />
              </Field>
              <div class="px-2 pt-1.5">/</div>
              <Field
                :rules="isRequired"
                name="sequenceNumber"
                class="flex flex-col gap-y-1"
                v-slot="{ field, errors: errs }"
              >
                <SimpleTextInput
                  :field="field"
                  :errors="errs"
                  :placeholder="$t('sequence')"
                  mask="###########"
                />
              </Field>
            </div>
          </div>
          <!-- unified number  -->
          <div class="w-full flex flex-col gap-3">
            <Field
              :rules="isRequired"
              name="personUnifiedNumber"
              v-slot="{ field, errors: errs }"
              v-if="values.selectFileIdentificater === 2"
            >
              <SimpleTextInput
                :field="field"
                :errors="errs"
                :label="$t('unifiedNumber')"
                input-class="md:w-1/2 lg:w-1/4"
                direction="flex-col"
                mask="############"
              />
            </Field>
            <Field
              :rules="isRequired"
              name="currentNationalityId"
              v-slot="{ field, errors: errs, handleChange }"
            >
              <SimpleDropdown
                :field="field"
                :errors="errs"
                :label="$t('nationality')"
                :options="countries"
                direction="flex-col"
                class="text-brand-black-secondary"
                @on-selection="(item) => handleChange(+item?.code)"
              ></SimpleDropdown>
            </Field>
            <Field
              :rules="isRequired"
              name="dateOfBirth"
              v-slot="{ field, errors: errs, handleChange }"
            >
              <SimpleDatepicker
                :field="field"
                :errors="errs"
                :label="$t('DateOfBirth')"
                max-date="__now__"
                class="text-brand-black-secondary"
                @on-change="(date) => handleChange(date)"
              >
              </SimpleDatepicker>
            </Field>
          </div>

          <div class="flex items-center justify-center my-6">
            <GRecaptcha ref="gRecaptchaRef" @successCallback="recaptchaCall"></GRecaptcha>
          </div>
        </CollapsibleSection>
        <div class="w-full flex justify-end">
          <FilledButton
            class="px-5 py-1.5 text-white"
            :disabled="isActive"
            as="button"
            type="submit"
          >
            {{ $t('search') }}
          </FilledButton>
        </div>
      </Form>
    </template>
  </div>
</template>

<script setup>
import FilledButton from '@/components/Buttons/Filled.vue'
import GRecaptcha from '@/components/GRecaptcha/index.vue'
import SimpleDatepicker from '@/components/SimpleForm/Datepicker.vue'
import SimpleDropdown from '@/components/SimpleForm/Dropdown.vue'
import SimpleTextInput from '@/components/SimpleForm/TextInput.vue'
import CollapsibleSection from '@/components/Ui/CollapsibleSection.vue'
import FilevaliditySkeleton from '@/views/file-validity/skeleton.vue'
import { useLookupsStore } from '@/stores/lookups'
import { notify } from 'notiwind'
import axiosInstance from '@/services'
import { Field, Form } from 'vee-validate'
import { nextTick, onMounted, ref } from 'vue'
import InfoBar from '@/components/Form/InfoBar.vue'
import { useI18n } from 'vue-i18n'

const countries = ref([])
const isLoading = ref(true)
const lookupsStore = useLookupsStore()
const gRecaptchaRef = ref()
const isActive = ref(true)
const passportTypes = ref()
const form = ref()

const i18n = useI18n()

const initialValues = {
  selectFileIdentificater: 1
}

onMounted(async () => {
  countries.value = await lookupsStore.fetchLookup('/api/lookup/countries/exceptGccForEida')
  passportTypes.value = await lookupsStore.fetchLookup('/api/lookup/passportTypes')
  isLoading.value = false
})

function isRequired(value) {
  if (value && `${value}`.trim()) {
    return true
  }

  return 'INVALID_REQUIRED'
}

const handleRadioClick = (resetField, value) => {
  initialValues.selectFileIdentificater = value
  nextTick(() => {
    if (value === 1) {
      form.value.resetForm({
        values: {
          selectFileIdentificater: 2,
          departmentCode: '',
          serviceYear: '',
          serviceCode: '',
          sequenceNumber: ''
        }
      })
    } else if (value === 2) {
      form.value.resetForm({
        values: {
          selectFileIdentificater: 3,
          personUnifiedNumber: ''
        }
      })
    }
    resetField()
    grecaptcha.reset()
  })
}

const recaptchaCall = (validate) => {
  if (!validate) {
    isActive.value = true
  }
  isActive.value = false
}

const onSubmit = async (values) => {
  isLoading.value = true
  const recaptcha = gRecaptchaRef.value.validate()
  if (!recaptcha) return
  if (values.dateOfBirth && values.dateOfBirth !== undefined && values.dateOfBirth !== null) {
    values.dateOfBirth = values.dateOfBirth.split('/').reverse().join('/')
  }

  const params = {
    lookupCurrentNationalityId: values?.currentNationalityId,
    dateOfBirth: values?.dateOfBirth,
    passportNumber: values?.passportNumber,
    lookupPassportTypeId: values?.passportTypeId,
    departmentCode: values?.departmentCode,
    sequence: values?.sequenceNumber,
    serviceCode: values?.serviceCode,
    year: values?.serviceYear,
    unifiedNumber: values?.personUnifiedNumber
  }

  const payload = Object.keys(params).reduce((acc, key) => {
    if (params[key] !== undefined && params[key] !== '') {
      acc[key] = params[key]
    }
    return acc
  }, {})

  try {
    const result = await axiosInstance.post(`/api/guest/visa/CanApplyPreEntryExtendVisa`, {
      body: payload
    })
    if (Object.keys(result).includes('errorMessage')) {
      notify(
        {
          group: 'error',
          title: i18n.t('NoRecordFound')
        },
        6000
      )
    }
    isLoading.value = false
  } catch (error) {
    isLoading.value = false
    console.error(error, 'error')
  }
  form.value.resetForm()
}
</script>

<style lang="scss" scoped></style>
